// src/services/authService.ts
import { auth } from "@/firebase";
import {
  signInWithCustomToken,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { UserRole } from "../types";

const RELATIVE_HANDSHAKE = "/api/partner-sso";
const ABSOLUTE_HANDSHAKE = (import.meta as any).env?.VITE_PARTNER_SSO_URL || "";

const safeSessionSet = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {}
};
const safeSessionGet = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSessionClear = () => {
  try {
    sessionStorage.clear();
  } catch {}
};

const resolveTenantFromClaims = (claims: Record<string, any>, fallbackLocationId: string) => {
  const claimLoc = (claims.locationId as string | undefined) || (claims.firmId as string | undefined);
  return claimLoc || fallbackLocationId;
};

const safeNavigateTenant = (tenantId: string, view: string = "dashboard") => {
  const nextUrl = `/location/${tenantId}/${view}`;
  window.history.replaceState({}, "", nextUrl);
  window.dispatchEvent(new Event("filershub:navigate"));
};

const setBestAuthPersistence = async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    return "local";
  } catch {}

  try {
    await setPersistence(auth, browserSessionPersistence);
    return "session";
  } catch {}

  await setPersistence(auth, inMemoryPersistence);
  return "memory";
};

const parseJsonOrText = async (res: Response) => {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return { json: await res.json().catch(() => ({})), text: "" };
  }
  const text = await res.text().catch(() => "");
  return { json: {}, text };
};

const fetchHandshake = async (email: string, locationId: string) => {
  const payload = JSON.stringify({ locationId, email });

  const attempt = async (url: string) => {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: payload,
    });

    const { json, text } = await parseJsonOrText(res);
    return { res, json, text, url };
  };

  // Try relative first (works only if Hosting rewrite exists)
  try {
    const a = await attempt(RELATIVE_HANDSHAKE);
    // If we got a 404, try absolute
    if (a.res.status !== 404) return a;
  } catch {}

  if (ABSOLUTE_HANDSHAKE) {
    // If your Cloud Run URL needs a path, set VITE_PARTNER_SSO_URL with the full path.
    return attempt(ABSOLUTE_HANDSHAKE);
  }

  // Last resort
  return attempt(RELATIVE_HANDSHAKE);
};

export const loginUser = async (email: string, locationId: string) => {
  const normalizedEmail = email.toLowerCase().trim();
  const normalizedLoc = locationId.trim();

  await setBestAuthPersistence();

  const { res, json, text, url } = await fetchHandshake(normalizedEmail, normalizedLoc);

  if (!res.ok) {
    const msg =
      (json as any)?.error ||
      (json as any)?.message ||
      (text ? `Handshake failed (${res.status}) from ${url}: ${text.slice(0, 200)}` : `Handshake failed (${res.status}) from ${url}`);
    throw new Error(msg);
  }

  const customToken = (json as any)?.customToken;
  if (!customToken) {
    const debug = text ? text.slice(0, 200) : JSON.stringify(json).slice(0, 200);
    throw new Error(`Token issuance failed. Endpoint ${url} returned no customToken. Response: ${debug}`);
  }

  safeSessionSet("FILERSHUB_SSO_LOC", normalizedLoc);
  safeSessionSet("FILERSHUB_SSO_EMAIL", normalizedEmail);

  await signInWithCustomToken(auth, customToken);

  const user = auth.currentUser;
  if (!user) throw new Error("Authentication did not complete.");

  const tokenResult = await user.getIdTokenResult(true);
  const tenantId = resolveTenantFromClaims(tokenResult.claims as Record<string, any>, normalizedLoc);

  safeSessionSet("FILERSHUB_SSO_LOC", tenantId);
  safeNavigateTenant(tenantId, "dashboard");

  return true;
};

export const registerUser = async (email: string, pass: string, fullName: string, role: UserRole, phone?: string) => {
  await setBestAuthPersistence();
  const userCredential = await createUserWithEmailAndPassword(auth, email, pass);
  return userCredential.user;
};

export const loginWithGoogle = async () => {
  await setBestAuthPersistence();
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);

  const tenantId = safeSessionGet("FILERSHUB_SSO_LOC");
  if (tenantId) safeNavigateTenant(tenantId, "dashboard");

  return result.user;
};

export const logoutUser = async () => {
  safeSessionClear();
  await signOut(auth);
};
